import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { RowClickedEvent } from 'ag-grid-community'
import { useTranslation } from 'react-i18next'
import { BarChartIcon, EllipsisIcon, SearchIcon, SyncIcon, UserIcon } from '@tractian/icons'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Typography
} from '@tractian/ui'

import type { PlantAssessment } from '@/features/assessments/api/assessment-list'
import { useUsersList } from '@/features/assessments/api/users-list'
import {
  AssessmentFiltersProvider,
  AssessmentFiltersToolbar,
  type AssessmentFilterModel
} from '@/features/assessments/components/assessment-filters'
import { AssessmentFormModal } from '@/features/assessments/containers/assessment-form-modal'
import { AddUserToSyncModal } from '@/features/assessments/containers/add-user-to-sync-modal'
import { AssessmentTable } from '@/features/assessments/components/assessment-table'
import { StartAssessmentModal } from '@/features/assessments/containers/start-assessment-modal'
import { useAssessmentList } from '@/features/assessments/hooks/use-assessment-list'
import { useAssessmentSort } from '@/features/assessments/hooks/use-assessment-sort'
import { useSyncedAssessmentIds } from '@/features/assessments/hooks/use-synced-assessment-ids'
import { useSyncSalesEngineerRoleUsers } from '@/features/assessments/hooks/use-sync-sales-engineer-role-users'
import { useFeature } from '@/features/feature-flags/hooks/use-feature'
import { AgTable } from '@/components/primitives/ag-table'
import { useFiltersContext } from '@/components/toolkit-filters/filters-provider'
import { paths } from '@/routes/routes'
import { buildAssessmentDateRangeParams } from '@/features/assessments/utils/assessment-date-range-params'
import { resolveRowClickAction } from '@/features/assessments/utils/resolve-row-click-action'

const TOGGLEABLE_COLS = [
  { field: 'dealOwnerUserID', label: 'Deal Owner' },
  { field: 'salesEngineerUserID', label: 'Sales Engineer' },
  { field: 'country', label: 'Country' },
  { field: 'updatedAt', label: 'Last updated' },
  { field: 'status', label: 'Status' }
]

const HIDDEN_COLS_KEY = 'assessmentTable:hiddenCols'

// Per tab: survives detail → back navigation, cleared when the tab closes.
const SEARCH_KEY = 'assessmentList:search'

// With multiPhase the Status column is gone: never offer it in the toggle,
// and ignore a stale `status` entry left in the persisted hidden columns.
const MULTI_PHASE_REMOVED_COLS = ['status']

function loadHiddenCols(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_COLS_KEY) ?? '[]')
  } catch {
    return []
  }
}

function parseYesNo(value: string | null | undefined): boolean | undefined {
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

export function AssessmentListContainer() {
  const { data: users } = useUsersList()
  const [page, setPage] = useState(1)

  return (
    <AssessmentFiltersProvider users={users} onFiltersChange={() => setPage(1)}>
      <AssessmentListInner page={page} setPage={setPage} />
    </AssessmentFiltersProvider>
  )
}

function AssessmentListInner({ page, setPage }: { page: number; setPage: (p: number) => void }) {
  const { t } = useTranslation('plantAssessment')
  const navigate = useNavigate()
  const { filterValues } = useFiltersContext<AssessmentFilterModel>()
  const multiPhase = useFeature('multiPhase')

  const [pageSize, setPageSize] = useState(20)
  const { sortField, sortOrder, onSortChanged } = useAssessmentSort({ multiPhase })
  const [search, setSearch] = useState(() => sessionStorage.getItem(SEARCH_KEY) ?? '')
  const [storedHiddenCols, setHiddenCols] = useState<string[]>(loadHiddenCols)
  const hiddenCols = multiPhase
    ? storedHiddenCols.filter(field => !MULTI_PHASE_REMOVED_COLS.includes(field))
    : storedHiddenCols
  const toggleableCols = multiPhase
    ? TOGGLEABLE_COLS.filter(col => !MULTI_PHASE_REMOVED_COLS.includes(col.field))
    : TOGGLEABLE_COLS

  const [formModalOpen, setFormModalOpen] = useState(false)
  const [formModalMode, setFormModalMode] = useState<'create' | 'edit'>('create')
  const [editingAssessment, setEditingAssessment] = useState<PlantAssessment | undefined>()

  const [startModalOpen, setStartModalOpen] = useState(false)
  const [startingAssessment, setStartingAssessment] = useState<PlantAssessment | undefined>()

  const [addUserModalOpen, setAddUserModalOpen] = useState(false)

  const { data: users } = useUsersList()
  const usersMap = new Map((users ?? []).map(u => [u.id, u]))

  const syncRoleUsers = useSyncSalesEngineerRoleUsers()

  const { data, isLoading } = useAssessmentList({
    page,
    limit: pageSize,
    sortField,
    sortOrder,
    search: search || undefined,
    status: multiPhase ? undefined : (filterValues.status ?? undefined),
    country: filterValues.country?.length > 0 ? filterValues.country : undefined,
    dealOwnerUserID:
      filterValues.dealOwnerUserID?.length > 0 ? filterValues.dealOwnerUserID : undefined,
    salesEngineerUserID:
      filterValues.salesEngineerUserID?.length > 0 ? filterValues.salesEngineerUserID : undefined,
    scopedDevices: filterValues.scopedDevices?.length > 0 ? filterValues.scopedDevices : undefined,
    hasStarted: parseYesNo(filterValues.hasStarted),
    isSubmitted: parseYesNo(filterValues.isSubmitted),
    ...buildAssessmentDateRangeParams({
      startedAt: filterValues.startedAt,
      lastUpdatedAt: filterValues.lastUpdatedAt,
      finishedAt: filterValues.finishedAt
    })
  })

  const syncedAssessmentIds = useSyncedAssessmentIds({
    assessments: data?.data ?? [],
    enabled: multiPhase
  })

  function handleSearchChange(value: string) {
    setSearch(value)
    sessionStorage.setItem(SEARCH_KEY, value)
    setPage(1)
  }

  function handleEditAssessment(assessment: PlantAssessment) {
    setFormModalMode('edit')
    setEditingAssessment(assessment)
    setFormModalOpen(true)
  }

  function handleRowClicked(e: RowClickedEvent<PlantAssessment>) {
    const action = resolveRowClickAction(
      e.data,
      (e.event?.target as HTMLElement | undefined) ?? undefined,
      multiPhase
    )
    if (action === 'navigate') navigate(paths.assessmentDetail(e.data!.id))
    else if (action === 'start') handleStartAssessment(e.data!)
  }

  function handleStartAssessment(assessment: PlantAssessment) {
    setStartingAssessment(assessment)
    setStartModalOpen(true)
  }

  function handleCreateAssessment() {
    setFormModalMode('create')
    setEditingAssessment(undefined)
    setFormModalOpen(true)
  }

  function setHiddenColsAndPersist(visibleNext: string[]) {
    // Keep entries for columns the flag hides, so turning it off restores them.
    const next = multiPhase
      ? [
          ...visibleNext,
          ...storedHiddenCols.filter(field => MULTI_PHASE_REMOVED_COLS.includes(field))
        ]
      : visibleNext
    setHiddenCols(next)
    localStorage.setItem(HIDDEN_COLS_KEY, JSON.stringify(next))
  }

  const columnToggle = (
    <AgTable.ColumnToggle
      storageKey={HIDDEN_COLS_KEY}
      columns={toggleableCols}
      hiddenCols={hiddenCols}
      onChange={setHiddenColsAndPersist}
    />
  )

  return (
    <div className="flex h-full min-h-0 flex-col gap-6 bg-gray-50 p-4">
      {/* Title row */}
      <div className="flex items-center justify-between">
        <Typography.Heading color="title" size="large">
          {t('assessmentList.title')}
        </Typography.Heading>
        <div className="flex items-center gap-3">
          <Button variant="primary" size="medium" onClick={handleCreateAssessment}>
            {t('assessmentList.createButton')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="medium"
                prefixIcon={<EllipsisIcon />}
                aria-label="Assessment actions"
                data-testid="assessment-actions-trigger"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="cursor-pointer gap-1.5"
                onClick={() => navigate(paths.mediaDashboard())}
              >
                <BarChartIcon className="text-primary" />
                <Typography.Text>{t('assessmentList.actions.mediaDashboard')}</Typography.Text>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer gap-1.5"
                disabled={syncRoleUsers.isPending}
                onClick={() => syncRoleUsers.mutate()}
              >
                <SyncIcon className="text-primary" />
                <Typography.Text>{t('assessmentList.actions.syncSEs')}</Typography.Text>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer gap-1.5"
                onSelect={() => setAddUserModalOpen(true)}
              >
                <UserIcon className="text-primary" />
                <Typography.Text>{t('assessmentList.actions.addUserToSync')}</Typography.Text>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {/* Search row */}
        <div className="flex shrink-0 items-center gap-3">
          <div className="flex-1">
            <Input value={search} onValueChange={handleSearchChange}>
              <Input.Field>
                <Input.Affix position="before">
                  <SearchIcon />
                </Input.Affix>
                <Input.Input placeholder={t('assessmentList.searchPlaceholder')} />
              </Input.Field>
            </Input>
          </div>
        </div>

        {/* Filter row */}
        <div className="shrink-0 py-3">
          <AssessmentFiltersToolbar />
        </div>

        {/* Table area */}
        <div className="mb-4 min-h-0 flex-1 rounded-lg border border-gray-200 [&_.ag-root-wrapper]:border-0">
          <AssessmentTable
            rowData={data?.data ?? []}
            isLoading={isLoading}
            totalCount={data?.count ?? 0}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={size => {
              setPageSize(size)
              setPage(1)
            }}
            onStartAssessment={handleStartAssessment}
            onEditAssessment={handleEditAssessment}
            onRowClicked={handleRowClicked}
            usersMap={usersMap}
            isUsersLoading={!users}
            hiddenCols={hiddenCols}
            multiPhase={multiPhase}
            syncedAssessmentIds={syncedAssessmentIds}
            footerRight={columnToggle}
            onSortChanged={onSortChanged}
          />
        </div>
      </div>

      <AssessmentFormModal
        open={formModalOpen}
        key={formModalMode === 'edit' ? editingAssessment?.id : 'create'}
        onClose={() => setFormModalOpen(false)}
        mode={formModalMode}
        assessment={editingAssessment}
      />

      <StartAssessmentModal
        open={startModalOpen}
        onClose={() => setStartModalOpen(false)}
        assessment={startingAssessment}
      />

      <AddUserToSyncModal open={addUserModalOpen} onClose={() => setAddUserModalOpen(false)} />
    </div>
  )
}
